import {
  Checkbox,
  Switch,
  InputChangeEvent,
  SwitchChangeEvent,
  CheckboxChangeEvent,
  TextAreaChangeEvent
} from '@progress/kendo-react-inputs';
import { useLocation } from 'react-router-dom';
import { Col, Container, Row, Tab, Tabs } from 'react-bootstrap';
import { useEffect, ComponentType, useContext, useState } from 'react';

import {
  AreaEntity,
  ResourceEntity
} from '../../../types/game-document/entities';
import { Algorithm } from '../../../types/algorithm';
import RequiredFields from '../../../types/required-fields';
import { GameDocumentContext } from '../../../contexts/game-document';
import { AreaAlgorithmEvent } from '../../../types/game-document/area';
import { StandardInput, StandardTextarea } from '../../../components/forms';
import EventAlgorithm from '../../../components/map/event-configuration/event--algorithm';

interface AreaEditorProps {
  entity: AreaEntity;
  resources: ResourceEntity[];
  requiredFields?: RequiredFields<AreaEntity>[];
  handleInputChange?: (
    e:
      | InputChangeEvent
      | TextAreaChangeEvent
      | CheckboxChangeEvent
      | SwitchChangeEvent
  ) => void;
  handleAlgorithmEventChange?: (
    algorithm: Algorithm,
    type: AreaAlgorithmEvent
  ) => void;
}

export const AreaEditorFeature: ComponentType<AreaEditorProps> = ({
  entity,
  resources,
  requiredFields,
  handleInputChange = () => {},
  handleAlgorithmEventChange = () => {},
  ...props
}: AreaEditorProps) => {
  const { state: stateLocation } = useLocation();

  const [activeTab, setActiveTab] = useState('information');

  const handleEventOnEntryChange = (algorithm: Algorithm) => {
    handleAlgorithmEventChange(algorithm, 'onEntry');
  };

  const handleEventOnExitChange = (algorithm: Algorithm) => {
    handleAlgorithmEventChange(algorithm, 'onExit');
  };

  useEffect(() => {
    if (!stateLocation?.openTab) return;

    setActiveTab(stateLocation?.openTab);
  }, [stateLocation?.openTab]);

  return (
    <Container className={'d-flex align-items-start flex-column'}>
      <Row className={'w-full'}>
        <Col md={12}>
          <Tabs
            unmountOnExit
            activeKey={activeTab}
            onSelect={(tab) => setActiveTab(tab ?? 'information')}
            id={'area-tabs'}
            className={'mb-3'}>
            <Tab eventKey={'information'} title={'Information'}>
              <div className={'d-flex flex-column gap-2'}>
                <div className={'row m-0 p-0'}>
                  <div className={'col-12 pl-0 pb-2'}>
                    <StandardInput
                      name={'titleResId'}
                      label={'Title'}
                      labelStyle={'fw-normal'}
                      value={
                        resources.find(
                          (resource) => resource.id === entity?.titleResId
                        )?.value ?? ''
                      }
                      onChange={handleInputChange}
                      validationMessage={
                        requiredFields?.find(
                          (item) => item.name === 'titleResId'
                        )?.errorMessage
                      }
                    />
                  </div>
                </div>
                <StandardTextarea
                  name={'description'}
                  label={'Description'}
                  labelStyle={'fw-normal'}
                  value={entity.description}
                  onChange={handleInputChange}
                />

                <div className={'d-flex flex-column gap-2'}>
                  <span
                    className={'fw-bold'}
                    style={{
                      fontSize: '18px'
                    }}>
                    Area Settings
                  </span>

                  <div className={'d-flex flex-column gap-1'}>
                    <span className={'fw-bold'}>Display</span>
                    <div className={'d-flex align-items-center gap-2'}>
                      <span>Show Area Boundary</span>
                      <Switch
                        size={'small'}
                        onChange={handleInputChange}
                        name={'isVisible'}
                        defaultChecked={entity.isVisible}
                      />
                    </div>
                    <div className={'d-flex align-items-center gap-2'}>
                      <span>Show Area Title</span>
                      <Switch
                        size={'small'}
                        onChange={handleInputChange}
                        name={'isTitleVisible'}
                        defaultChecked={entity.isTitleVisible}
                      />
                    </div>
                  </div>

                  <div className={'d-flex flex-column gap-1'}>
                    <span className={'fw-bold'}>Task Proximity</span>
                    <div className={'d-flex flex-column'}>
                      <span>
                        Enable or disable Task Proximity for this Area
                      </span>
                    </div>
                    <div className={'d-flex align-items-center gap-2'}>
                      <span>Task Proximity</span>
                      <Switch
                        size={'small'}
                        name={'disableProximity'}
                        onChange={handleInputChange}
                        defaultChecked={entity.disableProximity}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Tab>
            <Tab eventKey={'area-onentry'} title={'Entered'}>
              <EventAlgorithm
                onChange={handleEventOnEntryChange}
                algorithm={entity.events?.onEntry}
                action={'Area.onEntry'}
                description={`This runs when a player enters this area.`}
                shortcutText={'What happens when a player enters this area?'}
              />
            </Tab>
            <Tab eventKey={'area-onexit'} title={'Exited'}>
              <EventAlgorithm
                onChange={handleEventOnExitChange}
                algorithm={entity.events?.onExit}
                action={'Area.onExit'}
                description={`This runs when a player exits this area.`}
                shortcutText={'What happens when a player exits this area?'}
              />
            </Tab>
          </Tabs>
        </Col>
      </Row>
    </Container>
  );
};
